// ts-rest contract for the register of firm document versions (SEP-253 request 5).
//
// Two doors, and the `country-risk` register beside it holds the same shape.
// The read serves the LIVE label of every catalogue code, so the Administration
// card prints the whole catalogue in one call and a case form fills its version
// field without asking the investment manager. The write records one new label,
// and only Ops, Compliance and admin reach it.
//
// The register is APPEND-ONLY, so there is no update door and no delete door. A
// new version is a new row, and the old row keeps the label that governed a
// past case.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { firmDocumentVersionSchema } from '../onboarding-document-registers';
import { errorBody } from '../schemas/common';

const c = initContract();

/** One line of the Administration card: one catalogue code and its live label. */
export const firmDocumentVersionRowSchema = z.object({
    documentCode: z.string(),
    /** The catalogue label, so the card needs no second lookup. */
    documentLabel: z.string(),
    /** The live version, or null while the firm has published none. */
    version: firmDocumentVersionSchema.nullable(),
});
export type FirmDocumentVersionRow = z.infer<typeof firmDocumentVersionRowSchema>;

export const firmDocumentVersionsResponseSchema = z.object({
    /** Every catalogue code, in catalogue order, whether it holds a row or not. */
    documents: z.array(firmDocumentVersionRowSchema),
});
export type FirmDocumentVersionsResponse = z.infer<typeof firmDocumentVersionsResponseSchema>;

export const setFirmDocumentVersionBodySchema = z.object({
    documentCode: z.string().min(1),
    versionLabel: z.string().min(1),
    /** The plain calendar day the version takes effect, `YYYY-MM-DD`. */
    effectiveFrom: z.string().min(1),
});
export type SetFirmDocumentVersionBody = z.infer<typeof setFirmDocumentVersionBodySchema>;

export const firmDocumentVersionsContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/admin/firm-document-versions',
            responses: { 200: firmDocumentVersionsResponseSchema, 403: errorBody },
            summary:
                'The live version label the firm holds for every item of its literature (SEP-253 request 5)',
        },
        set: {
            method: 'POST',
            path: '/admin/firm-document-versions',
            body: setFirmDocumentVersionBodySchema,
            responses: {
                200: z.object({ id: z.string() }),
                400: errorBody,
                403: errorBody,
            },
            summary:
                'Publish the version label of one item of firm literature. The live row is superseded, and nothing is edited in place (SEP-253 request 5)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
